// Exam Prep hub — landing page for JEE/NEET features. The navy→charcoal
// hero stays fixed across every theme (the "this is the premium section"
// signature); the gold accent is tuned per active student theme
// (examPrepTheme.js) so it never clashes with a dark/bright theme's own
// palette.
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, PenSquare, ChevronRight, Sparkles } from 'lucide-react';
import { examPrep, examPrepSurfaceGradient, getExamPrepColors, getExamPrepGoldGradient } from './examPrepTheme';
import { useStudent } from './StudentApp';

const CARDS = [
  {
    to: 'quizzes', icon: ClipboardList, title: 'Practice',
    desc: 'Build a timed mock test from real exam questions, by chapter and difficulty.',
  },
  // Formula Sheet — commented out for now, not removed. The page
  // (FormulaSheetPage.jsx) and its route are untouched; this just drops
  // the entry point from the hub grid.
  // {
  //   to: 'exam-prep/formula-sheet', icon: Sigma, title: 'Formula Sheet',
  //   desc: 'Every formula pulled from lessons you’ve studied, in one place.',
  // },
  {
    to: 'exam-prep/practice', icon: PenSquare, title: 'Question Bank',
    desc: 'Browse real past-year questions by subject, chapter and difficulty, and reveal the answer and solution.',
  },
];

export default function ExamPrepHub() {
  const navigate = useNavigate();
  const { theme } = useStudent();
  const colors = getExamPrepColors(theme?.id);
  const goldGradient = getExamPrepGoldGradient(theme?.id);
  return (
    <div className="space-y-6 max-w-4xl" data-testid="exam-prep-hub">
      <section
        className="rounded-3xl overflow-hidden relative px-6 py-8 sm:px-8 sm:py-10"
        style={{ background: examPrepSurfaceGradient }}
        data-testid="exam-prep-hero">
        <div
          aria-hidden="true"
          className="absolute -top-16 -right-12 w-72 h-72 rounded-full opacity-[0.12] blur-3xl pointer-events-none"
          style={{ background: `radial-gradient(circle, ${colors.gold}, transparent 70%)` }}
        />
        <div className="relative flex items-center gap-2 mb-3">
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.14em]"
            style={{ background: goldGradient, color: examPrep.charcoal }}>
            <Sparkles className="w-3 h-3" /> Exam Prep
          </span>
        </div>
        <h1 className="relative text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
          JEE &amp; NEET, built into your desk
        </h1>
        <p className="relative text-[13px] mt-2 max-w-lg" style={{ color: 'rgba(255,255,255,0.72)' }}>
          Exam-mode quizzes, a formula sheet drawn from your lessons, and step-by-step
          grading on your own worked solutions.
        </p>
      </section>

      <div className="grid sm:grid-cols-3 gap-3">
        {CARDS.map(c => (
          <button
            key={c.to}
            onClick={() => navigate(`/student/${c.to}`)}
            data-testid={`exam-prep-card-${c.to.split('/').pop()}`}
            className="text-left rounded-2xl p-5 border transition-all hover:shadow-lg hover:-translate-y-0.5"
            style={{ background: 'var(--st-card)', borderColor: 'var(--st-border)' }}>
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
              style={{ background: colors.goldTint, color: colors.gold }}>
              <c.icon className="w-5 h-5" />
            </div>
            <div className="flex items-center gap-1.5">
              <h2 className="text-[14px] font-bold" style={{ fontFamily: 'var(--st-font-heading)', color: 'var(--st-ink)' }}>
                {c.title}
              </h2>
              <ChevronRight className="w-3.5 h-3.5 opacity-40" />
            </div>
            <p className="text-[12px] mt-1.5 leading-snug" style={{ color: 'var(--st-mute)' }}>{c.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
